import { parseNumeric, postRequest } from '../../../shared/utils';

/**
 * Fetches a role id from configuration by its role name.
 */
export async function fetchRoleId(
  screenGridId: number,
  roleName: string,
  configSchema: string,
): Promise<number | null> {
  const result = await postRequest('/api/records', {
    columns: ['id'],
    screenGridId,
    schema: configSchema,
    table: 'mbd_roles',
    key: 'role_name',
    value: roleName,
  });

  const rows = result?.data?.[`${configSchema}_mbd_roles`] ?? [];
  const roleId = rows
    .map((row: { id?: unknown }) => parseNumeric(row?.id))
    .find((id: number | null): id is number => id != null);

  return roleId ?? null;
}

/**
 * Fetches user ids that are assigned to the provided role id.
 */
export async function fetchRoleUserIds(
  screenGridId: number,
  roleId: number,
  configSchema: string,
): Promise<number[]> {
  if (roleId == null) return [];

  const result = await postRequest('/api/records', {
    columns: ['user_id', 'role_id'],
    screenGridId,
    schema: configSchema,
    table: 'mbd_user_to_role',
    filterModel: {
      items: [{ field: 'role_id', value: roleId, operator: '=' }],
      logicOperator: 'and',
    },
  });

  const rows = result?.data?.[`${configSchema}_mbd_user_to_role`] ?? [];
  const userIds = rows
    .map((row: { user_id?: unknown }) => parseNumeric(row?.user_id))
    .filter((id: number | null): id is number => id != null);

  return userIds;
}

/**
 * Fetches user ids subscribed to the provided profit center.
 */
export async function fetchSubscribedUserIds(
  screenGridId: number,
  pcnrId: number,
  configSchema: string,
): Promise<number[]> {
  const result = await postRequest('/api/records', {
    columns: ['user_id', 'pcnr_id'],
    screenGridId,
    schema: configSchema,
    table: 'mbd_users_to_pcnr',
    filterModel: {
      items: [{ field: 'pcnr_id', value: pcnrId, operator: '=' }],
      logicOperator: 'and',
    },
  });

  const rows = result?.data?.[`${configSchema}_mbd_users_to_pcnr`] ?? [];
  const userIds = rows
    .map((row: { user_id?: unknown }) => parseNumeric(row?.user_id))
    .filter((id: number | null): id is number => id != null);

  return userIds;
}

/**
 * Returns distinct user ids present in both input lists.
 */
export function intersectUserIds(left: number[], right: number[]): number[] {
  if (!left.length || !right.length) return [];
  const rightSet = new Set(right);
  return [...new Set(left)].filter((id) => rightSet.has(id));
}

/**
 * Resolves AV recipients by intersecting role members and PCNR subscribers.
 */
export async function resolveAvRecipients(params: {
  screenGridId: number;
  pcnrId: number;
  configSchema: string;
}): Promise<number[]> {
  const { screenGridId, pcnrId, configSchema } = params;

  const avRoleId = await fetchRoleId(screenGridId, 'AV', configSchema);
  if (avRoleId == null) {
    return [];
  }

  const [roleUserIds, subscribedUserIds] = await Promise.all([
    fetchRoleUserIds(screenGridId, avRoleId, configSchema),
    fetchSubscribedUserIds(screenGridId, pcnrId, configSchema),
  ]);

  return intersectUserIds(roleUserIds, subscribedUserIds);
}

/**
 * Resolves recipients for a specific role and profit center by intersecting
 * role members with profit-center subscribers.
 */
export async function resolveRoleRecipientsByProfitCenter(params: {
  screenGridId: number;
  pcnrId: number;
  roleName: string;
  configSchema: string;
}): Promise<number[]> {
  const { screenGridId, pcnrId, roleName, configSchema } = params;

  const roleId = await fetchRoleId(screenGridId, roleName, configSchema);
  if (roleId == null) {
    return [];
  }

  const [roleUserIds, subscribedUserIds] = await Promise.all([
    fetchRoleUserIds(screenGridId, roleId, configSchema),
    fetchSubscribedUserIds(screenGridId, pcnrId, configSchema),
  ]);

  return intersectUserIds(roleUserIds, subscribedUserIds);
}

/**
 * Resolves recipients by AV+PCNR first, then NPI Manager fallback.
 */
export async function resolveRecipientsWithNpiManagerFallback(params: {
  screenGridId: number;
  pcnrId: number;
  configSchema: string;
}): Promise<number[]> {
  const { screenGridId, pcnrId, configSchema } = params;

  const recipients = await resolveAvRecipients({ screenGridId, pcnrId, configSchema });

  if (recipients.length > 0) {
    return recipients;
  }

  const npiManagerRoleId = await fetchRoleId(screenGridId, 'NPI Manager', configSchema);

  if (npiManagerRoleId == null) {
    return [];
  }

  return fetchRoleUserIds(screenGridId, npiManagerRoleId, configSchema);
}